import { useEffect, useMemo, useState } from "react";
import { MultiSelect } from "primereact/multiselect";

import { Category } from "./Category";
import { fetchCategoriesByDepartment } from "./categoriesApi";
import TicketIcon from "../tickets/TicketIcon";
import { NoResultsFoundText } from "../../common/components/noResultsFound/NoResultsFound";

interface CategoryMultiselectProps {
  departmentId: string;
  level: 0 | 1 | 2;
  restrictToIds?: string[];
  restrictToParentIds?: string[];
  restrictToGrandParentIds?: string[];
  value?: string[];
  onChange: (categoryIds: string[]) => void;
}

const categoryTemplate = (option: Category) => (
  <div className="flex align-items-center gap-2">
    <TicketIcon ticketType={option.ticketType} />
    <span>{option.name}</span>
  </div>
);

export default function CategoryMultiselect({
  departmentId,
  level,
  restrictToIds,
  restrictToParentIds,
  restrictToGrandParentIds,
  value,
  onChange,
}: CategoryMultiselectProps) {
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const options = useMemo(() => {
    let levelCategories = allCategories.filter((c) => c.level === level);
    if (restrictToIds && restrictToIds.length > 0) {
      levelCategories = levelCategories.filter((c) => restrictToIds.includes(c.id));
    }
    if (restrictToParentIds && restrictToParentIds.length > 0) {
      levelCategories = levelCategories.filter((c) => c.parentId && restrictToParentIds.includes(c.parentId));
    }
    if (restrictToGrandParentIds && restrictToGrandParentIds.length > 0) {
      const allowedParentIds = allCategories
        .filter((c) => c.level === level - 1 && c.parentId && restrictToGrandParentIds.includes(c.parentId))
        .map((c) => c.id);
      levelCategories = levelCategories.filter((c) => c.parentId && allowedParentIds.includes(c.parentId));
    }
    return levelCategories;
  }, [allCategories, level, restrictToIds, restrictToParentIds, restrictToGrandParentIds]);
  const tooManyOptions = level === 2 && options.length > 200;
  const selectedCategories = options.filter((c) => value?.includes(c.id));

  useEffect(() => {
    const fetchCategories = async () => {
      if (departmentId) {
        const categories = await fetchCategoriesByDepartment(departmentId);
        const activeCategories = categories.filter((c) => c.isActive);
        setAllCategories(activeCategories);
      }
    };
    fetchCategories();
  }, [departmentId]);

  return (
    <MultiSelect
      optionLabel="name"
      scrollHeight="300px"
      showClear
      filter
      filterBy="name"
      itemTemplate={level === 2 ? categoryTemplate : undefined}
      options={tooManyOptions ? [] : options}
      value={selectedCategories}
      emptyFilterMessage={NoResultsFoundText}
      emptyMessage={tooManyOptions ? "Filtreaza dupa categorie primara/secundara pentru a vedea optiunile." : NoResultsFoundText}
      onChange={(e) => onChange(e.value.map((c: Category) => c.id))}
    />
  );
}
